<script lang="ts" setup>
  import { computed, watch } from 'vue'

  import { useColorMode } from '@vueuse/core'
  import { afterLoginSuccess } from '@/helpers/lifecycleInjection'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import AwaitTasksOverlay from '@/components/AwaitTasksOverlay.vue'
  import { useAppState } from '@/composables/useAppState.ts'
  import MaintenanceView from '@/views/MaintenanceView.vue'
  import UpdateNotificationView from '@/components/layout/UpdateNotificationView.vue'
  import { useConfigStore } from '@/stores/Config.store'
  import { getDefaultLevel, getKnownLoggers, getLogger, LogLevel, resetLevels, setLevel, setMinimumLevel } from '@/composables/useLogger.ts'
  import ImpersonationNotificationView from '@/components/layout/ImpersonationNotificationView.vue'
  import ErrorView from '@/views/ErrorView.vue'
  import { useTitropEvents } from '@/composables/useTitropEvents.ts'
  import { VERSION_URL } from '@/composables/useVersionCheck.ts'

  const { state } = useColorMode({ initialValue: 'light', storageKey: null })
  // if (store.value === 'auto') store.value = new URL(location.href).searchParams.has('dark') ? 'dark' : 'light'
  const isDark = computed(() => state.value === 'dark')

  useTitropEvents().onLoginSuccess.on(() => {
    void afterLoginSuccess()
  })

  //Some trace GLOBALS
  window.__TITROP__.app_build_version = __BUILD_VERSION__ ?? 'DEV'
  window.__TITROP__.app_build_date = __BUILD_DATE__ ?? new Date().toISOString()
  window.__TITROP__.app_default_backend = __BACKEND_URL__
  window.__TITROP__.app_default_api = __API_URL__
  window.__TITROP__.ci_built = __CI__
  window.__TITROP__.ci_branch = __CI_COMMIT_BRANCH__
  window.__TITROP__.ci_commit_sha = __CI_COMMIT_SHA__
  window.__TITROP__.ci_commit_sha_short = __CI_COMMIT_SHORT_SHA__
  window.__TITROP__.ci_pipeline = __CI_PIPELINE_ID__
  window.__TITROP__.env_node = __ENV__
  window.__TITROP__.env_mode = __MODE__
  window.__TITROP__.logger = {
    globalLevel: getDefaultLevel(),
    getKnownLogger: getKnownLoggers,
    resetLevels: resetLevels,
    fullDebug: () => {
      setMinimumLevel(LogLevel.Debug)
    },
    setMinLevelGlobal: setMinimumLevel,
    setLevelGlobal: setLevel,
    getLogger: getLogger
  }

  const appstate = useAppState()

  const maintenance = useConfigStore().appGet<boolean>('maintenance_mode', false)
  let serverPull: unknown = undefined

  const serverReachable: () => Promise<boolean> = async () => {
    try {
      const res = await fetch(VERSION_URL, {
        method: 'GET',
        cache: 'no-store',
        keepalive: false
      })
      return res.ok
    } catch {
      return false
    }
  }

  watch(
    maintenance,
    (m) => {
      if (m) {
        const x = async () => {
          if (await serverReachable()) {
            void useConfigStore().$refresh()
          } else {
            serverPull = setTimeout(x, 5000)
          }
        }
        void x()
      } else {
        if (typeof serverPull !== 'undefined') clearInterval(serverPull as number)
      }
    },
    { immediate: true }
  )
</script>
<template>
  <div>
    <ErrorView />
    <UpdateNotificationView />
    <ImpersonationNotificationView />
    <template v-if="!maintenance">
      <Message
        v-if="isDark"
        :life="10000"
        class="bg-gray-900 text-gray-300 w-full text-center z-50 border border-rose-500 rounded-lg px-1">
        Der DarkMode ist experimentell - Fehler sind zu erwarten
      </Message>

      <div
        v-if="appstate.backendUrlNonDefault.value"
        :key="appstate.backendUrl.value.toString()">
        <Message
          :pt-options="{ mergeProps: true, mergeSections: false }"
          :pt:content="'w-full justify-center'"
          :pt:text="['text-xl font-semibold']"
          class="w-full z-50 px-1"
          severity="error">
          Sie nutzen ein anderes Backend ({{ appstate.backendUrl }}) als das Standard-Backend! Bitte äußerste Vorsicht walten lassen!

          <ButtonDefault
            class="ml-4"
            label="Standard verwenden"
            outlined
            severity="info"
            size="small"
            @click="appstate.resetBackend()" />
        </Message>
      </div>
      <component :is="$route.meta.layout" />
      <AwaitTasksOverlay />
    </template>
    <MaintenanceView v-else />
  </div>
</template>
